import React, { useCallback, useMemo, useState } from 'react';

import { Trans, useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { usePreserveModalForm } from '@/components/PreserveModalForm';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import useResponse from '@/hooks/useResponse';
import { PeerFeedbackStatusEnum } from '@/types/schema';

import { useCreatePeerFeedbackMutation } from '../../../../../graphql/mutations/generated/createFeedback';
import { useUpdatePeerFeedbackMutation } from '../../../../../graphql/mutations/generated/updateFeedback';
import { CreateFeedbackDialogProps, InitialFormValueType } from '../types';
import {
  getCreateFeedbackFormInitialValues,
  getFields,
  getSectionLayout,
  validateMeetingForm,
} from '../utils';

const useCreateFeedback = (props: CreateFeedbackDialogProps) => {
  const { initialFormValues, onClose, onSuccess, userIds } = props;
  const { t } = useTranslation();
  const [selectedValues, setSelectedValues] = useState<InitialFormValueType>({
    ...getCreateFeedbackFormInitialValues(userIds),
    ...initialFormValues,
  });
  const { handleResponse } = useResponse();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const { showSnackbar } = useSnackbar();
  const [{ fetching }, createPeerFeedback] = useCreatePeerFeedbackMutation();
  const [{ fetching: updating }, updatePeerFeedback] = useUpdatePeerFeedbackMutation();
  const [submitRef, setSubmitRef] = useState<() => void>(() => {});

  const { onFormChange, onCloseModal, modalClosePrompt } = usePreserveModalForm({
    onClose,
  });

  const onSubmit = React.useCallback(async () => {
    const mediasWithoutId = selectedValues.medias?.map(({ mediaId, ...rest }) => rest);
    const res = await createPeerFeedback({
      input: {
        receiverUserId: selectedValues.receiverUserId[0],
        feedbackMessage: selectedValues.feedbackMessage,
        visibleToManager: selectedValues.visibleToManager,
        learningContentIds: selectedValues.learningContentIds,
        skillRatings: selectedValues.skillRatings,
        mindsetRatings: selectedValues.mindsetRatings,
        status: PeerFeedbackStatusEnum.Completed,
        attachments: mediasWithoutId || [],
        mentions: selectedValues.mentions || [],
      },
    });
    handleResponse({
      condition: !!res.data?.createPeerFeedback?.peerFeedback?.id,
      onSuccess: () => {
        showSnackbar({
          message: (
            <Trans
              i18nKey={'Feedback:CreateFeedback.SuccessMessage'}
              values={{ user: res.data?.createPeerFeedback?.peerFeedback?.receiverUser?.name }}
            />
          ),
        });
        onSuccess?.();
        onClose();
      },
      onError: () => {
        showDefaultErrorSnackbar();
      },
    });
  }, [
    createPeerFeedback,
    handleResponse,
    onClose,
    onSuccess,
    selectedValues.feedbackMessage,
    selectedValues.learningContentIds,
    selectedValues.medias,
    selectedValues.mentions,
    selectedValues.mindsetRatings,
    selectedValues.receiverUserId,
    selectedValues.skillRatings,
    selectedValues.visibleToManager,
    showDefaultErrorSnackbar,
    showSnackbar,
  ]);

  const onUpdate = React.useCallback(async () => {
    const mediasWithoutId = selectedValues.medias?.map(({ mediaId, ...rest }) => rest);
    const res = await updatePeerFeedback({
      input: {
        feedbackMessage: selectedValues.feedbackMessage,
        visibleToManager: selectedValues.visibleToManager,
        learningContentIds: selectedValues.learningContentIds,
        peerFeedbackId: selectedValues.peerFeedbackId!,
        skillRatings: selectedValues.skillRatings,
        mindsetRatings: selectedValues.mindsetRatings,
        status: PeerFeedbackStatusEnum.Completed,
        attachments: {
          set: mediasWithoutId || [],
        },
        mentions: {
          set: selectedValues.mentions || [],
        },
      },
    });
    handleResponse({
      condition: !!res.data?.updatePeerFeedback?.peerFeedback?.id,
      onSuccess: () => {
        showSnackbar({
          message: (
            <Trans
              i18nKey={
                selectedValues.status === PeerFeedbackStatusEnum.Requested
                  ? 'Feedback:CreateFeedback.SuccessMessage'
                  : 'Feedback:CreateFeedback.UpdateMessage'
              }
              values={{ user: res.data?.updatePeerFeedback?.peerFeedback?.receiverUser?.name }}
            />
          ),
        });
        onClose();
      },
      onError: () => {
        showDefaultErrorSnackbar();
      },
    });
  }, [
    handleResponse,
    onClose,
    selectedValues,
    showDefaultErrorSnackbar,
    showSnackbar,
    updatePeerFeedback,
  ]);

  const onChange = useCallback(
    (response: any) => {
      const { values } = response;
      onFormChange(response);
      setSelectedValues(values);
    },
    [onFormChange],
  );

  const fields = useMemo(
    () => getFields({ t, isEditing: !!initialFormValues }),
    [initialFormValues, t],
  );
  const sections = useMemo(
    () => getSectionLayout({ t, isRequestPresent: !!initialFormValues?.requestMessage }),
    [initialFormValues?.requestMessage, t],
  );
  const validate = useMemo(
    () => (values: InitialFormValueType) => validateMeetingForm(t, values),
    [t],
  );
  return {
    fields,
    validate,
    sections,
    onChange,
    onSubmit,
    submitRef,
    setSubmitRef,
    fetching: fetching || updating,
    selectedValues,
    onUpdate,
    setSelectedValues,
    onCloseModal,
    modalClosePrompt,
  };
};

export default useCreateFeedback;
